import axiosClient from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";

export type HomePageSectionLayout = "HORIZONTAL_SCROLL" | "GRID";
export type HomePageSectionPlacement = "TOP" | "BOTTOM";
export type HomePageProductSource =
  | "MANUAL"
  | "TOP_DEALS"
  | "TOP_RANKING"
  | "NEW_ARRIVALS"
  | "FEATURED"
  | "LATEST"
  | "CATEGORY"
  | "FILTERED";
export type HomePageBannerPlacement = "TOP" | "AFTER_SECTION";
export type HomePageBannerDisplayMode = "FULL_WIDTH" | "CAROUSEL";

export interface HomePageSectionMeta {
  id: string;
  title: string;
  description: string | null;
  sectionType: HomePageSectionLayout;
  placement: HomePageSectionPlacement;
  sourceConfig?: Record<string, unknown> | null;
  publicSlug: string;
  bannerAccentColor: string | null;
  productSource: HomePageProductSource;
  productLimit: number;
}

export interface HomePageBannerDto {
  id: string;
  sortOrder: number;
  imageUrl: string;
  bannerAccentColor: string | null;
  title: string | null;
  subtitle: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
  displayMode: HomePageBannerDisplayMode;
  placement: HomePageBannerPlacement;
  publicSlug: string;
}

export interface HomePagePublicRow {
  section: HomePageSectionMeta;
  products: unknown[];
  bannersAfter: HomePageBannerDto[];
}

/** Resolved buyer homepage payload from GET /homepage */
export interface HomePagePublicResponse {
  success: boolean;
  topBanners: HomePageBannerDto[];
  bottomBanners: HomePageBannerDto[];
  rows: HomePagePublicRow[];
}

export interface AdminHomePageSection {
  id: string;
  sortOrder: number;
  enabled: boolean;
  placement: HomePageSectionPlacement;
  title: string;
  description: string | null;
  sectionType: HomePageSectionLayout;
  publicSlug: string;
  productSource: HomePageProductSource;
  productLimit: number;
  sourceConfig: Record<string, unknown> | null;
  bannerAccentColor: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  manualProducts: { id: string; productId: string; sortOrder: number }[];
}

export interface AdminHomePageBanner {
  id: string;
  sortOrder: number;
  enabled: boolean;
  imageUrl: string;
  bannerAccentColor: string | null;
  title: string | null;
  subtitle: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
  publicSlug: string;
  placement: HomePageBannerPlacement;
  displayMode: HomePageBannerDisplayMode;
  afterSectionId: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  bannerProducts: { id: string; productId: string; sortOrder: number }[];
}

/**
 * Nest `TransformInterceptor` wraps JSON as `{ success: true, data: <controller body>, timestamp }`.
 * Axios `response.data` is that envelope; controller payloads (e.g. `{ sections }`) live in `data.data`.
 */
function unwrapNestResponse<T>(axiosData: unknown): T {
  if (
    axiosData !== null &&
    typeof axiosData === "object" &&
    "data" in axiosData &&
    (axiosData as { success?: boolean }).success === true
  ) {
    return (axiosData as { data: T }).data;
  }
  return axiosData as T;
}

class HomepageService {
  async getPublicHomepage(): Promise<HomePagePublicResponse> {
    const { data } = await axiosClient.get(ENDPOINTS.HOMEPAGE.PUBLIC);
    return unwrapNestResponse<HomePagePublicResponse>(data);
  }

  async getSectionMetaBySlug(slug: string): Promise<{
    success: boolean;
    section: HomePageSectionMeta;
  }> {
    const { data } = await axiosClient.get(ENDPOINTS.HOMEPAGE.SECTION_META(slug));
    return unwrapNestResponse<{ success: boolean; section: HomePageSectionMeta }>(
      data
    );
  }

  async getSectionProductsBySlug(slug: string): Promise<{
    success: boolean;
    section: HomePageSectionMeta;
    products: unknown[];
  }> {
    const { data } = await axiosClient.get(
      ENDPOINTS.HOMEPAGE.SECTION_PRODUCTS(slug)
    );
    return unwrapNestResponse<{
      success: boolean;
      section: HomePageSectionMeta;
      products: unknown[];
    }>(data);
  }

  async getBannerProductsBySlug(slug: string): Promise<{
    success: boolean;
    banner: HomePageBannerDto;
    products: unknown[];
  }> {
    const { data } = await axiosClient.get(ENDPOINTS.HOMEPAGE.BANNER_PRODUCTS(slug));
    return unwrapNestResponse<{
      success: boolean;
      banner: HomePageBannerDto;
      products: unknown[];
    }>(data);
  }

  async adminListSections(): Promise<{
    success: boolean;
    sections: AdminHomePageSection[];
  }> {
    const { data } = await axiosClient.get(ENDPOINTS.HOMEPAGE.ADMIN_SECTIONS);
    return unwrapNestResponse<{
      success: boolean;
      sections: AdminHomePageSection[];
    }>(data);
  }

  async adminCreateSection(body: {
    title: string;
    description?: string;
    sectionType?: HomePageSectionLayout;
    placement?: HomePageSectionPlacement;
    publicSlug: string;
    /** Ignored by API; sections always use manual products. */
    productSource?: HomePageProductSource;
    productLimit?: number;
    sourceConfig?: Record<string, unknown>;
    bannerAccentColor?: string;
    enabled?: boolean;
    startsAt?: string | null;
    endsAt?: string | null;
  }): Promise<{ success: boolean; section: AdminHomePageSection }> {
    const { data } = await axiosClient.post(
      ENDPOINTS.HOMEPAGE.ADMIN_SECTIONS,
      body
    );
    return unwrapNestResponse<{ success: boolean; section: AdminHomePageSection }>(
      data
    );
  }

  async adminUpdateSection(
    id: string,
    body: Partial<{
      title: string;
      description: string | null;
      sectionType: HomePageSectionLayout;
      placement: HomePageSectionPlacement;
      publicSlug: string;
      productSource: HomePageProductSource;
      productLimit: number;
      sourceConfig: Record<string, unknown> | null;
      bannerAccentColor: string | null;
      enabled: boolean;
      startsAt: string | null;
      endsAt: string | null;
    }>
  ): Promise<{ success: boolean; section: AdminHomePageSection }> {
    const { data } = await axiosClient.patch(
      ENDPOINTS.HOMEPAGE.ADMIN_SECTION(id),
      body
    );
    return unwrapNestResponse<{ success: boolean; section: AdminHomePageSection }>(
      data
    );
  }

  async adminDeleteSection(id: string): Promise<{ success: boolean }> {
    const { data } = await axiosClient.delete(
      ENDPOINTS.HOMEPAGE.ADMIN_SECTION(id)
    );
    return unwrapNestResponse<{ success: boolean }>(data);
  }

  async adminReorderSections(
    orderedIds: string[]
  ): Promise<{ success: boolean; sections: AdminHomePageSection[] }> {
    const { data } = await axiosClient.patch(
      ENDPOINTS.HOMEPAGE.ADMIN_SECTIONS_REORDER,
      { orderedIds }
    );
    return unwrapNestResponse<{
      success: boolean;
      sections: AdminHomePageSection[];
    }>(data);
  }

  async adminSetSectionProducts(
    sectionId: string,
    productIds: string[]
  ): Promise<{ success: boolean; section: AdminHomePageSection }> {
    const { data } = await axiosClient.put(
      ENDPOINTS.HOMEPAGE.ADMIN_SECTION_PRODUCTS(sectionId),
      { productIds }
    );
    return unwrapNestResponse<{ success: boolean; section: AdminHomePageSection }>(
      data
    );
  }

  async adminListBanners(): Promise<{
    success: boolean;
    banners: AdminHomePageBanner[];
  }> {
    const { data } = await axiosClient.get(ENDPOINTS.HOMEPAGE.ADMIN_BANNERS);
    return unwrapNestResponse<{
      success: boolean;
      banners: AdminHomePageBanner[];
    }>(data);
  }

  async adminCreateBanner(body: {
    sortOrder?: number;
    imageUrl: string;
    bannerAccentColor?: string;
    publicSlug: string;
    title?: string;
    subtitle?: string;
    ctaText?: string;
    ctaUrl?: string;
    placement: HomePageBannerPlacement;
    displayMode?: HomePageBannerDisplayMode;
    afterSectionId?: string | null;
    enabled?: boolean;
    startsAt?: string | null;
    endsAt?: string | null;
  }): Promise<{ success: boolean; banner: AdminHomePageBanner }> {
    const { data } = await axiosClient.post(
      ENDPOINTS.HOMEPAGE.ADMIN_BANNERS,
      body
    );
    return unwrapNestResponse<{ success: boolean; banner: AdminHomePageBanner }>(
      data
    );
  }

  async adminUpdateBanner(
    id: string,
    body: Partial<{
      sortOrder: number;
      imageUrl: string;
      bannerAccentColor: string | null;
      publicSlug: string;
      title: string | null;
      subtitle: string | null;
      ctaText: string | null;
      ctaUrl: string | null;
      placement: HomePageBannerPlacement;
      displayMode: HomePageBannerDisplayMode;
      afterSectionId: string | null;
      enabled: boolean;
      startsAt: string | null;
      endsAt: string | null;
    }>
  ): Promise<{ success: boolean; banner: AdminHomePageBanner }> {
    const { data } = await axiosClient.patch(
      ENDPOINTS.HOMEPAGE.ADMIN_BANNER(id),
      body
    );
    return unwrapNestResponse<{ success: boolean; banner: AdminHomePageBanner }>(
      data
    );
  }

  async adminDeleteBanner(id: string): Promise<{ success: boolean }> {
    const { data } = await axiosClient.delete(
      ENDPOINTS.HOMEPAGE.ADMIN_BANNER(id)
    );
    return unwrapNestResponse<{ success: boolean }>(data);
  }

  async adminReorderBanners(
    orderedIds: string[]
  ): Promise<{ success: boolean; banners: AdminHomePageBanner[] }> {
    const { data } = await axiosClient.patch(
      ENDPOINTS.HOMEPAGE.ADMIN_BANNERS_REORDER,
      { orderedIds }
    );
    return unwrapNestResponse<{
      success: boolean;
      banners: AdminHomePageBanner[];
    }>(data);
  }

  async adminSetBannerProducts(
    bannerId: string,
    productIds: string[]
  ): Promise<{ success: boolean; banner: AdminHomePageBanner }> {
    const { data } = await axiosClient.put(
      ENDPOINTS.HOMEPAGE.ADMIN_BANNER_PRODUCTS(bannerId),
      { productIds }
    );
    return unwrapNestResponse<{ success: boolean; banner: AdminHomePageBanner }>(
      data
    );
  }
}

export const homepageService = new HomepageService();
